// GENERATED from messaging-platform/packages/chat-core/src/chat-store.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
import { BehaviorSubject, Observable, Subject, combineLatest, map } from 'rxjs';
import { prepareImage, uploadToHub, type UploadOptions } from './file-upload';
import { ChatCache, type QueuedMessage } from './chat-cache';
import { conversationPhotoRef, photoOf } from './avatar';
import {
  ActionCard,
  ConversationSummary,
  HubEvent,
  PatientChatActivity,
  HubOrg,
  HubReady,
  HubRequestError,
  HubTransport,
  ConversationReceipt,
  MemberView,
  SearchHit,
  MessageTick,
  AttachmentView,
  BillingPerson,
  BillingRelation,
  MessageView,
  NotifyLevel,
  PinView,
  FilingView,
  UnlinkScope,
  PatientMention,
  PushRegistration,
  ThreadMessage,
} from './hub-types';
import type { MentionablePerson } from './people-mentions';

/** How long the `@` picker's list of people for a conversation is reused before it is asked for again. */
const MENTIONABLE_TTL_MS = 60_000;

/**
 * All chat state in the browser, and every change to it (tasks/messaging-hub-architecture.md §6.1, §9; M1).
 *
 * Framework-free (RxJS only), so it can move to a shared package with MedicAI Connect (M5). One store, one source of
 * truth — Connect's review flagged its split BehaviorSubject stores; this keeps one `state$`.
 *
 *   conversations   the list with unread counts; kept current by server events and refreshed on (re)connect
 *   threads         loaded pages per conversation, oldest first; the latest 50 on open, 50 more on scroll-up (O10)
 *   sending         shown at once as pending (optimistic), replaced by the server's copy on the reply, marked failed
 *                   (with retry) on error; a retry reuses the same clientMessageId, so the hub stores it once
 *   reading         while a conversation is the ACTIVE one and the page is visible, new messages are marked read
 */

export interface Thread {
  messages: ThreadMessage[];
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
  /** an older page, opened from a search hit: the newest messages are NOT in this thread until it is reopened */
  historic?: boolean;
  /** the message a search hit asked for, for the view to scroll to and mark */
  pinnedSeq?: number | null;
}

export interface ChatState {
  me: HubReady | null;
  conversations: Record<string, ConversationSummary>;
  threads: Record<string, Thread>;
  /** conversationId -> principalId -> typist */
  typing: Record<string, Record<string, { name: string; until: number }>>;
  online: Record<string, true>;
  activeConversationId: string | null;
  /** ticks: conversationId -> principalId -> how far that person has got (live events; see marksOf) */
  receipts: Record<string, Record<string, { deliveredSeq: number; readSeq: number }>>;
}

const EMPTY: ChatState = { me: null, conversations: {}, threads: {}, typing: {}, online: {}, activeConversationId: null, receipts: {} };
const TYPING_MS = 6000;
/** Delivery acknowledgements are batched: a burst of messages costs one call. */
const DELIVERY_DEBOUNCE_MS = 400;
export const PAGE = 50;

export const newClientMessageId = (): string =>
  `c-${typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`}`;

/** Sorted by seq, pending (no real seq yet) last, with duplicates by id removed (the newest copy kept). */
export function mergeMessages(existing: readonly ThreadMessage[], incoming: readonly ThreadMessage[]): ThreadMessage[] {
  const byId = new Map<string, ThreadMessage>();
  for (const m of existing) byId.set(m.id, m);
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => {
    if (!!a.pending !== !!b.pending) return a.pending ? 1 : -1;
    return a.seq - b.seq;
  });
}

/**
 * What a conversation is ordered by: its latest message, or — when nobody has written in it yet — when it was made.
 *
 * An empty room used to compare as '' and sink below every conversation that had ever been used, which is exactly
 * backwards for the one case that produces empty rooms: a referral chat opened when the referral is sent, so that
 * somebody can be the first to write in it (2026-09-21).
 */
const sortKeyOf = (c: ConversationSummary): string => c.lastMessageAt ?? c.createdAt ?? '';

export class ChatStore {
  private readonly state = new BehaviorSubject<ChatState>(EMPTY);
  /** what this device keeps for offline: the list, recent messages, queued sends and drafts */
  readonly cache = new ChatCache();
  /** conversation -> the highest seq this device has, waiting to be acknowledged (ticks) */
  private readonly pendingDelivery = new Map<string, number>();
  /** conversationId -> the patient a money card's Chat button wants tagged when the thread opens it */
  private readonly prefills = new Map<string, PatientMention>();
  private deliveryTimer: ReturnType<typeof setTimeout> | null = null;
  readonly state$: Observable<ChatState> = this.state.asObservable();
  private readonly incoming = new Subject<MessageView>();
  /**
   * Every message someone else sends, as it arrives, once its conversation is known here: what an app needs to play a
   * sound. Whether to (muted, the conversation already open, the user's own setting) is the app's to decide.
   */
  readonly incoming$: Observable<MessageView> = this.incoming.asObservable();
  private readonly pinsChanged = new Subject<string>();
  /** A conversation whose pinned messages changed (anyone pinned or unpinned one): fetch them again with `pins`. */
  readonly pinsChanged$: Observable<string> = this.pinsChanged.asObservable();
  private readonly filingChanged = new Subject<string>();
  /** A conversation that started or stopped being filed to patients: ask again with `filing`. */
  readonly filingChanged$: Observable<string> = this.filingChanged.asObservable();
  private readonly prefilled = new Subject<string>();
  /** A patient is waiting to be tagged in this conversation (for a thread that already shows it). */
  readonly prefilled$: Observable<string> = this.prefilled.asObservable();
  private transport: HubTransport | null = null;
  private detach: (() => void)[] = [];
  private visible = true;

  get snapshot(): ChatState {
    return this.state.value;
  }

  private set(patch: Partial<ChatState> | ((s: ChatState) => Partial<ChatState>)): void {
    const s = this.state.value;
    this.state.next({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) });
  }

  // ── wiring ─────────────────────────────────────────────────────────────────────────

  attach(transport: HubTransport): void {
    this.dispose();
    this.transport = transport;
    this.detach.push(
      transport.onReady((ready) => void this.onReady(ready)),
      transport.onEvent((e) => this.onEvent(e)),
    );
  }

  dispose(): void {
    for (const d of this.detach) d();
    this.detach = [];
    this.transport = null;
    this.state.next(EMPTY);
  }

  /**
   * Signing out on this device: everything kept for offline goes, including anything still queued. A shared computer
   * must not show one person's chat to the next.
   */
  forgetOnThisDevice(): void {
    this.cache.clear();
  }

  private get hub(): HubTransport {
    if (!this.transport) throw new HubRequestError('NOT_CONNECTED');
    return this.transport;
  }

  /** Whether the page is visible (the app sets this from document visibility); unread is only cleared when it is. */
  setVisible(visible: boolean): void {
    this.visible = visible;
    const id = this.state.value.activeConversationId;
    if (visible && id) void this.markReadToEnd(id);
    // Someone looking at the app sees new messages here, so the hub does not push to them (M4).
    void this.transport?.request('presence:visible', { visible }).catch(() => undefined);
  }

  /** On every (re)connect: who I am, then a fresh list, then catch up the threads that are open. */
  private async onReady(me: HubReady): Promise<void> {
    this.set({ me });
    // Show what this device already knows (instantly, and even offline), then let the hub correct it.
    this.cache.open(me.principalId);
    if (!Object.keys(this.state.value.conversations).length && this.cache.conversations.length) {
      this.set({ conversations: Object.fromEntries(this.cache.conversations.map((c) => [c.id, c])) });
    }
    // A new socket starts as not visible on the hub: tell it where we are.
    void this.transport?.request('presence:visible', { visible: this.visible }).catch(() => undefined);
    await this.refreshList();
    // Presence events only report changes; who is online NOW is asked once per (re)connect, for the DM partners shown.
    await this.refreshPresence(Object.values(this.state.value.conversations).flatMap((c) => (c.members ?? []).map((m) => m.principalId)));
    for (const id of Object.keys(this.state.value.threads)) {
      if (this.state.value.threads[id]?.loaded) await this.catchUp(id);
    }
    // Everything this device already holds counts as delivered, so senders see two ticks after a reconnect.
    this.noteDeliveredForAll();
    // Anything typed while disconnected goes now, oldest first.
    await this.flushQueue();
  }

  // ── selectors ─────────────────────────────────────────────────────────────────────

  orgByRef(ref: string | null | undefined): HubOrg | null {
    return this.state.value.me?.orgs.find((o) => o.ref === ref) ?? null;
  }

  conversationsOf$(orgId$: Observable<string | null>): Observable<ConversationSummary[]> {
    return combineLatest([this.state$, orgId$]).pipe(
      map(([s, orgId]) =>
        Object.values(s.conversations)
          .filter((c) => c.orgId === orgId)
          .sort((a, b) => sortKeyOf(b).localeCompare(sortKeyOf(a))),
      ),
    );
  }

  unreadOf$(orgId$: Observable<string | null>): Observable<number> {
    return this.conversationsOf$(orgId$).pipe(
      map((list) => list.filter((c) => c.access === 'participant' && !this.isMuted(c)).reduce((n, c) => n + c.unread, 0)),
    );
  }

  isMuted(c: ConversationSummary, now = Date.now()): boolean {
    return !!c.mutedUntil && Date.parse(c.mutedUntil) > now;
  }

  /** A display name for any conversation: the channel or patient name, or the other people in a DM. */
  // ticks: delivered and read (2026-09-20)

  /**
   * How far the others have got in this conversation: the hub's own figure (the minimum over every other participant),
   * raised by the live receipts this page has seen since. A live event names ONE person, so those only count once this
   * page has heard from as many people as the conversation has others.
   */
  private marksOf(c: ConversationSummary): { deliveredSeq: number; readSeq: number; others: number } {
    const server = c.receipts ?? { deliveredSeq: 0, readSeq: 0, others: 0 };
    const live = this.state.value.receipts[c.id];
    if (!live || !server.others || Object.keys(live).length < server.others) return server;
    const people = Object.values(live);
    return {
      others: server.others,
      deliveredSeq: Math.max(server.deliveredSeq, Math.min(...people.map((p) => p.deliveredSeq))),
      readSeq: Math.max(server.readSeq, Math.min(...people.map((p) => p.readSeq))),
    };
  }

  /**
   * The tick for one of MY messages: sent (the hub has it), delivered (the other side's device has it), read.
   * null for someone else's message, a system line, or a conversation with nobody else in it.
   *
   * TICKS ARE FOR TWO-SIDED CONVERSATIONS (a direct message, a referral chat, in-app support). In a room — a channel,
   * a group message, a practice's patient chat — one colleague on leave would hold every message at one tick for ever,
   * which says nothing useful; those show `readCountOf` ("read by 3 of 8") instead.
   */
  tickOf(conversationId: string, message: ThreadMessage): MessageTick | null {
    const me = this.state.value.me?.principalId;
    if (!me || message.kind !== 'user' || message.sender?.principalId !== me) return null;
    if (message.failed) return 'failed';
    if (message.pending) return 'sending';
    const c = this.state.value.conversations[conversationId];
    if (!c || !c.receipts?.others) return 'sent';
    if (!this.twoSided(c)) return 'sent';
    const marks = this.marksOf(c);
    if (marks.readSeq >= message.seq) return 'read';
    if (marks.deliveredSeq >= message.seq) return 'delivered';
    return 'sent';
  }

  /** One other side, so a tick means something definite: a direct message, a referral chat, in-app support. */
  private twoSided(c: ConversationSummary): boolean {
    if (c.type === 'direct' || c.type === 'referral' || c.type === 'support' || c.type === 'network') return true;
    return (c.receipts?.others ?? 0) === 1;
  }

  /**
   * In a room: how many of the others have read one of MY messages, and how many there are ("read by 3 of 8"). null
   * for a two-sided conversation (a tick says it), someone else's message, or one still on its way.
   */
  readCountOf(conversationId: string, message: ThreadMessage): { read: number; of: number } | null {
    const me = this.state.value.me?.principalId;
    if (!me || message.kind !== 'user' || message.sender?.principalId !== me || message.pending || message.failed) return null;
    const c = this.state.value.conversations[conversationId];
    if (!c?.receipts?.others || this.twoSided(c)) return null;
    const live = this.state.value.receipts[c.id] ?? {};
    const seen = Object.values(live).filter((p) => p.readSeq >= message.seq).length;
    // The hub's own figure is the minimum over everyone, so when it has passed the message, everyone has read it.
    const read = c.receipts.readSeq >= message.seq ? c.receipts.others : Math.min(seen, c.receipts.others);
    return { read, of: c.receipts.others };
  }

  // ── profile photos (2026-09-20) ───────────────────────────────────────────────────

  /**
   * Where to fetch someone's photo, or null when they have none (show their initials instead). The ticket comes with
   * `hub:ready` and covers every photo this page shows; the URL carries the photo's version, so it can be cached hard
   * and a new photo is simply a new URL.
   */
  avatarUrl(principalId: string, avatar: string | null | undefined): string | null {
    const base = this.transport?.baseUrl?.();
    const ticket = this.state.value.me?.avatarTicket;
    if (!base || !ticket || !avatar) return null;
    return `${base}/avatars/${principalId}/${avatar}/${ticket}`;
  }

  /** Where to fetch a group's photo, or null (show its icon instead). The same ticket as people's photos. */
  conversationAvatarUrl(conversationId: string, avatar: string | null | undefined): string | null {
    const base = this.transport?.baseUrl?.();
    const ticket = this.state.value.me?.avatarTicket;
    if (!base || !ticket || !avatar) return null;
    return `${base}/avatars/conv/${conversationId}/${avatar}/${ticket}`;
  }

  /** The photo to show beside a conversation in a list: a group's own, or the other person's in a direct chat. */
  photoUrlOf(c: ConversationSummary): string | null {
    const group = conversationPhotoRef(c);
    if (group) return this.conversationAvatarUrl(c.id, group);
    const person = photoOf(c, this.state.value.me?.principalId ?? null);
    return person ? this.avatarUrl(person.principalId, person.avatar) : null;
  }

  /**
   * Sets a group's photo; the hub allows whoever may rename the group. Shrunk to a small square first, as a person's
   * photo is. The group's members are told by the hub (conv:updated); this page shows it at once.
   */
  async setConversationPhoto(conversationId: string, file: Blob, fileName = 'photo.jpg'): Promise<string> {
    const base = this.transport?.baseUrl?.();
    if (!base) throw new HubRequestError('NOT_CONNECTED');
    const small = await prepareImage(file, fileName, { maxDimension: 256, quality: 0.85 });
    const contentType = small.file.type || 'image/jpeg';
    const { uploadPath } = await this.hub.request<{ uploadPath: string }>('conv:avatarSet', { conversationId, contentType, sizeBytes: small.file.size });
    let res: Response;
    try {
      res = await fetch(`${base}${uploadPath}`, { method: 'PUT', body: small.file, headers: { 'content-type': contentType } });
    } catch {
      throw new HubRequestError('UPLOAD_FAILED');
    }
    if (!res.ok) throw new HubRequestError(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? 'UPLOAD_FAILED');
    const { ref } = (await res.json()) as { ref: string };
    const c = this.state.value.conversations[conversationId];
    if (c) this.upsertConversation({ ...c, avatar: ref });
    return ref;
  }

  /** Removes a group's photo; it falls back to its icon. */
  async clearConversationPhoto(conversationId: string): Promise<void> {
    await this.hub.request('conv:avatarClear', { conversationId });
    const c = this.state.value.conversations[conversationId];
    if (c) {
      const { avatar: _gone, ...rest } = c;
      this.upsertConversation(rest);
    }
  }

  /**
   * Sets this person's own profile photo. Hand it a picture: it is shrunk to a small square first (prepareImage), so a
   * phone photo does not travel at full size. Rejects FILE_TYPE / FILE_TOO_LARGE the same way files do.
   */
  async setAvatar(file: Blob, fileName = 'photo.jpg'): Promise<string> {
    const base = this.transport?.baseUrl?.();
    if (!base) throw new HubRequestError('NOT_CONNECTED');
    const small = await prepareImage(file, fileName, { maxDimension: 256, quality: 0.85 });
    const contentType = small.file.type || 'image/jpeg';
    const { uploadPath } = await this.hub.request<{ uploadPath: string }>('avatar:set', { contentType, sizeBytes: small.file.size });
    let res: Response;
    try {
      res = await fetch(`${base}${uploadPath}`, { method: 'PUT', body: small.file, headers: { 'content-type': contentType } });
    } catch {
      throw new HubRequestError('UPLOAD_FAILED');
    }
    if (!res.ok) throw new HubRequestError(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? 'UPLOAD_FAILED');
    const { ref } = (await res.json()) as { ref: string };
    this.setAvatarOf(this.state.value.me?.principalId ?? '', ref);
    // Colleagues' lists should show it now, not on their next reload.
    await this.hub.request('avatar:announce', { avatar: ref }).catch(() => undefined);
    return ref;
  }

  /** Removes this person's photo; they fall back to their initials. */
  async clearAvatar(): Promise<void> {
    await this.hub.request('avatar:clear', {});
    this.setAvatarOf(this.state.value.me?.principalId ?? '', null);
  }

  /** Puts a photo (or its absence) into everything already on screen: the list's members, and loaded messages. */
  private setAvatarOf(principalId: string, avatar: string | null): void {
    if (!principalId) return;
    this.set((s) => ({
      conversations: Object.fromEntries(Object.entries(s.conversations).map(([id, c]) => [id, c.members?.some((m) => m.principalId === principalId)
        ? { ...c, members: c.members.map((m) => (m.principalId === principalId ? { ...m, ...(avatar ? { avatar } : { avatar: undefined }) } : m)) }
        : c])),
      threads: Object.fromEntries(Object.entries(s.threads).map(([id, t]) => [id, {
        ...t,
        messages: t.messages.map((m) => (m.sender?.principalId === principalId
          ? { ...m, sender: { ...m.sender, ...(avatar ? { avatar } : { avatar: undefined }) } }
          : m)),
      }])),
    }));
  }

  private readonly mentionableCache = new Map<string, { at: number; people: MentionablePerson[] }>();
  private readonly mentionableLoading = new Map<string, Promise<MentionablePerson[]>>();

  /**
   * Who may be @-mentioned in this conversation (the hub decides: people who can already read it). Cached for a
   * minute per conversation; call it when a conversation opens so the picker has names at once.
   */
  mentionable(conversationId: string): Promise<MentionablePerson[]> {
    const hit = this.mentionableCache.get(conversationId);
    if (hit && Date.now() - hit.at < MENTIONABLE_TTL_MS) return Promise.resolve(hit.people);
    const loading = this.mentionableLoading.get(conversationId);
    if (loading) return loading;
    const p = this.hub.request<MentionablePerson[]>('conv:mentionable', { conversationId })
      .then((people) => {
        const list = people.map((x) => ({ principalId: x.principalId, name: x.name, ...(x.avatar ? { avatar: x.avatar } : {}), ...(x.clinicalUserId ? { clinicalUserId: x.clinicalUserId } : {}) }));
        this.mentionableCache.set(conversationId, { at: Date.now(), people: list });
        return list;
      })
      .finally(() => this.mentionableLoading.delete(conversationId));
    this.mentionableLoading.set(conversationId, p);
    return p;
  }

  /** What `mentionable` last returned for this conversation (empty until it has been asked), for rendering. */
  mentionableNow(conversationId: string): MentionablePerson[] {
    return this.mentionableCache.get(conversationId)?.people ?? [];
  }

  /**
   * The people a message @-mentions, with the names to highlight: from the `@` picker's list, the reader themself
   * (whom that list leaves out), and whoever has written in the thread. An id with no name known is left out (its
   * "@Name" then shows as plain text).
   */
  mentionedPeople(conversationId: string, m: Pick<MessageView, 'mentions'>): { principalId: string; name: string }[] {
    const ids = m.mentions?.principals ?? [];
    if (!ids.length) return [];
    const names = new Map<string, string>();
    const me = this.state.value.me;
    if (me) names.set(me.principalId, me.displayName);
    for (const x of this.state.value.threads[conversationId]?.messages ?? []) if (x.sender?.name) names.set(x.sender.principalId, x.sender.name);
    for (const p of this.mentionableNow(conversationId)) names.set(p.principalId, p.name);
    return ids.filter((id) => names.get(id)).map((id) => ({ principalId: id, name: names.get(id)! }));
  }

  /** Who has received and read this conversation, person by person (for "read by 3 of 4"). */
  receiptsFor(conversationId: string): Promise<ConversationReceipt[]> {
    return this.hub.request<ConversationReceipt[]>('conv:receipts', { conversationId });
  }

  /** Tells the hub what this device now has, so the senders' ticks move. Batched. */
  private noteDelivered(conversationId: string, seq: number): void {
    if (seq <= (this.pendingDelivery.get(conversationId) ?? 0)) return;
    this.pendingDelivery.set(conversationId, seq);
    if (this.deliveryTimer) return;
    this.deliveryTimer = setTimeout(() => {
      this.deliveryTimer = null;
      const items = [...this.pendingDelivery.entries()].map(([id, s]) => ({ conversationId: id, seq: s })).slice(0, 200);
      this.pendingDelivery.clear();
      if (!items.length || !this.transport) return;
      void this.hub.request('conv:delivered', { items }).catch(() => undefined);
    }, DELIVERY_DEBOUNCE_MS);
  }

  /** Everything this device already has, whenever a connection is made: their senders see two ticks. */
  private noteDeliveredForAll(): void {
    for (const c of Object.values(this.state.value.conversations)) {
      if (c.access === 'participant' && c.lastMessageSeq) this.noteDelivered(c.id, c.lastMessageSeq);
    }
  }

  titleOf(c: ConversationSummary): string {
    if (c.type === 'channel') return c.name ?? 'Channel';
    if (c.type === 'patient') return c.name ?? 'Patient';
    if (c.type === 'referral') return [c.name ?? 'Patient', c.referral?.counterpart].filter(Boolean).join(' · ');
    // support (M7): 'MedicAI Support' for the practice user, '<who asked> · <practice>' for an agent
    if (c.type === 'support') return c.name ?? 'MedicAI Support';
    // network 1-on-1 (2026-09-22): the other person, and their practice when they have one
    if (c.type === 'network') return [c.network?.counterpart.name ?? c.name ?? 'Direct message', c.network?.counterpart.practiceName].filter(Boolean).join(' · ');
    // a patient's own conversation (patient app): the patient at the practice, the practice at the patient; it has no
    // member list to name it from, so never "Just you"
    // billing chats (2026-10-05): the room by the other side's org; a 1:1 or group by its people (or name) and that org
    if (c.type === 'billing') {
      const other = c.billing?.counterpartName;
      if (c.billing?.kind === 'room') return c.name ?? other ?? 'Billing';
      const people = c.billing?.kind === 'group' && c.name ? c.name : (c.members ?? []).map((m) => m.name).filter(Boolean).join(', ');
      return [people || 'Billing', other].filter(Boolean).join(' · ');
    }
    if (c.type === 'patient_direct') {
      const practiceSide = c.patientDirect?.side !== 'patient';
      return c.name ?? (practiceSide ? c.patientDirect?.patientName : c.patientDirect?.practiceName) ?? (practiceSide ? 'Patient' : 'Your practice');
    }
    const names = (c.members ?? []).map((m) => m.name).filter(Boolean);
    return names.length ? names.join(', ') : 'Just you';
  }

  async refreshPresence(principalIds: string[]): Promise<void> {
    const ids = [...new Set(principalIds)].slice(0, 200);
    if (!ids.length) return;
    try {
      const online = await this.hub.request<string[]>('presence:of', { principalIds: ids });
      this.set((s) => {
        const next = { ...s.online };
        for (const id of ids) delete next[id];
        for (const id of online) next[id] = true;
        return { online: next };
      });
    } catch {
      // Presence is decoration; a failure leaves the dots as they were.
    }
  }

  // ── list ──────────────────────────────────────────────────────────────────────────

  async refreshList(): Promise<void> {
    const list = await this.hub.request<ConversationSummary[]>('conv:list');
    const conversations: Record<string, ConversationSummary> = {};
    for (const c of list) conversations[c.id] = c;
    // The device keeps the list, so chat opens with something in it next time, network or not.
    this.cache.saveConversations(list);
    this.set((s) => ({
      conversations,
      // Forget threads of conversations that are gone (removed, deleted, left the practice).
      threads: Object.fromEntries(Object.entries(s.threads).filter(([id]) => conversations[id])),
    }));
  }

  private upsertConversation(c: ConversationSummary): void {
    this.set((s) => ({ conversations: { ...s.conversations, [c.id]: c } }));
  }

  private removeConversation(id: string): void {
    this.set((s) => {
      const { [id]: _c, ...conversations } = s.conversations;
      const { [id]: _t, ...threads } = s.threads;
      return { conversations, threads, activeConversationId: s.activeConversationId === id ? null : s.activeConversationId };
    });
  }

  async refreshConversation(id: string): Promise<void> {
    try {
      this.upsertConversation(await this.hub.request<ConversationSummary>('conv:get', { conversationId: id }));
    } catch (e) {
      if (e instanceof HubRequestError && e.code === 'NOT_FOUND') this.removeConversation(id);
      else throw e;
    }
  }

  // ── threads ───────────────────────────────────────────────────────────────────────

  private setThread(id: string, patch: Partial<Thread>): void {
    this.set((s) => {
      const t = s.threads[id] ?? { messages: [], hasMore: false, loading: false, loaded: false };
      return { threads: { ...s.threads, [id]: { ...t, ...patch } } };
    });
    // Keep the device's copy of what was said, for the next time there is no network.
    if (patch.messages && !this.state.value.threads[id]?.historic) this.cache.saveThread(id, patch.messages);
  }

  /** Makes `id` the active conversation: loads its latest page once, and marks it read while visible. */
  async open(id: string): Promise<void> {
    this.set({ activeConversationId: id });
    const t = this.state.value.threads[id];
    if (!t?.loaded && !t?.loading) {
      // What this device already has, shown while the hub answers — and all there is when it cannot.
      const kept = this.cache.threadOf(id);
      if (kept.length && !t?.messages.length) this.setThread(id, { messages: kept, loaded: true });
      this.setThread(id, { loading: true });
      try {
        const page = await this.hub.request<{ messages: MessageView[]; hasMore: boolean }>('msg:history', { conversationId: id, limit: PAGE });
        this.setThread(id, { messages: mergeMessages(this.state.value.threads[id]?.messages ?? [], page.messages), hasMore: page.hasMore, loaded: true, loading: false });
      } catch (e) {
        // Offline with a page from the cache: keep showing it rather than emptying the thread.
        const kept = this.cache.threadOf(id).length > 0;
        this.setThread(id, { loading: false, ...(kept ? { loaded: true, hasMore: true } : {}) });
        throw e;
      }
    }
    if (this.visible) await this.markReadToEnd(id);
  }

  close(id: string): void {
    if (this.state.value.activeConversationId === id) this.set({ activeConversationId: null });
  }

  /** The 50 before the oldest loaded message (scrolling up). */
  async loadOlder(id: string): Promise<void> {
    const t = this.state.value.threads[id];
    if (!t?.loaded || t.loading || !t.hasMore) return;
    const oldest = t.messages.find((m) => !m.pending);
    if (!oldest) return;
    this.setThread(id, { loading: true });
    try {
      const page = await this.hub.request<{ messages: MessageView[]; hasMore: boolean }>('msg:history', { conversationId: id, beforeSeq: oldest.seq, limit: PAGE });
      this.setThread(id, { messages: mergeMessages(this.state.value.threads[id]?.messages ?? [], page.messages), hasMore: page.hasMore, loading: false });
    } catch (e) {
      this.setThread(id, { loading: false });
      throw e;
    }
  }

  /** After a reconnect: the latest page again, merged in (messages that arrived while offline). */
  private async catchUp(id: string): Promise<void> {
    try {
      const page = await this.hub.request<{ messages: MessageView[]; hasMore: boolean }>('msg:history', { conversationId: id, limit: PAGE });
      this.setThread(id, { messages: mergeMessages(this.state.value.threads[id]?.messages ?? [], page.messages) });
    } catch (e) {
      if (e instanceof HubRequestError && e.code === 'NOT_FOUND') this.removeConversation(id);
    }
  }

  private async markReadToEnd(id: string): Promise<void> {
    const c = this.state.value.conversations[id];
    if (!c || c.access !== 'participant' || c.lastMessageSeq == null || c.lastReadSeq >= c.lastMessageSeq) return;
    const seq = c.lastMessageSeq;
    this.upsertConversation({ ...c, lastReadSeq: seq, unread: 0 });
    try {
      await this.hub.request('conv:read', { conversationId: id, seq });
    } catch {
      // Not worth surfacing: the next list refresh has the server's count.
    }
  }

  // ── sending ───────────────────────────────────────────────────────────────────────

  private fileGrant: ((conversationId: string, clientMessageId: string) => Promise<string>) | null = null;

  /**
   * How this app gets leave to send files in a chat that needs it (a referral chat with a clinician outside MedicAI,
   * `referral.filesByGrant`): its own server spends one of the referral's documents on the message and signs leave for
   * it. Asked before each attempt (a retry or a queued send too); the server gives the same message the same leave.
   */
  setFileGrantProvider(provider: ((conversationId: string, clientMessageId: string) => Promise<string>) | null): void {
    this.fileGrant = provider;
  }

  private async grantFor(conversationId: string, clientMessageId: string, files: number): Promise<string | undefined> {
    const referral = this.state.value.conversations[conversationId]?.referral;
    if (!files || !referral?.filesByGrant || referral.filesAllowed) return undefined;
    if (!this.fileGrant) throw new HubRequestError('FILE_GRANT_REQUIRED');
    return this.fileGrant(conversationId, clientMessageId);
  }

  /**
   * Sends a message (shown at once as pending). `patients` are its # mentions: the hub links the message into each
   * patient's chat once it is stored. Resolves to the stored message, or null when there was nothing to send.
   */
  async send(
    conversationId: string,
    body: string,
    options: {
      replyToMessageId?: string | null;
      mentions?: string[];
      clientMessageId?: string;
      patients?: PatientMention[];
      /** files already uploaded with uploadFile (M5) */
      attachments?: AttachmentView[];
    } = {},
  ): Promise<MessageView | null> {
    const me = this.state.value.me;
    const text = body.trim();
    const files = options.attachments ?? [];
    if (!me || (!text && !files.length)) return null;
    const clientMessageId = options.clientMessageId ?? newClientMessageId();
    const placeholder: ThreadMessage = {
      id: clientMessageId,
      clientMessageId,
      seq: Number.MAX_SAFE_INTEGER,
      conversationId,
      kind: 'user',
      sender: { principalId: me.principalId, name: me.displayName },
      body: text,
      mentions: { principals: options.mentions ?? [], ...(options.patients?.length ? { patients: options.patients.map((p) => p.patientRef) } : {}) },
      replyToMessageId: options.replyToMessageId ?? null,
      editedAt: null,
      deletedAt: null,
      createdAt: new Date().toISOString(),
      reactions: [],
      pending: true,
      ...(options.patients?.length ? { patientMentions: options.patients } : {}),
      ...(files.length ? { attachments: files } : {}),
    };
    this.setThread(conversationId, { messages: mergeMessages(this.withoutId(conversationId, clientMessageId), [placeholder]) });
    // No connection: keep it on the device (through a reload) and send it when the hub is back.
    if (!this.connected) {
      this.cache.enqueue({
        conversationId, clientMessageId, body: text, replyToMessageId: options.replyToMessageId ?? null,
        ...(options.mentions?.length ? { mentions: options.mentions } : {}),
        ...(options.patients?.length ? { patients: options.patients } : {}),
        ...(files.length ? { attachments: files } : {}),
        at: new Date().toISOString(),
      });
      return null;
    }
    try {
      // Only a message with files waits for leave; text goes straight out.
      const grant = files.length ? await this.grantFor(conversationId, clientMessageId, files.length) : undefined;
      const saved = await this.hub.request<MessageView>('msg:send', {
        conversationId,
        body: text,
        clientMessageId,
        ...(grant ? { grant } : {}),
        ...(options.replyToMessageId ? { replyToMessageId: options.replyToMessageId } : {}),
        ...(options.mentions?.length ? { mentions: options.mentions } : {}),
        ...(options.patients?.length ? { patients: options.patients } : {}),
        ...(files.length ? { attachmentIds: files.map((f) => f.id) } : {}),
      });
      this.setThread(conversationId, { messages: mergeMessages(this.withoutId(conversationId, clientMessageId), [saved]) });
      this.cache.dequeue(clientMessageId);
      this.noteNewMessage(saved);
      return saved;
    } catch (e) {
      this.setThread(conversationId, {
        messages: mergeMessages(this.withoutId(conversationId, clientMessageId), [{ ...placeholder, pending: true, failed: true }]),
      });
      // A message the hub refused for good (too long, read-only) must not be retried for ever; one that failed on the
      // way is kept for the next connection.
      const code = e instanceof HubRequestError ? e.code : '';
      if (code === 'TIMEOUT' || code === 'NOT_CONNECTED' || code === 'INTERNAL' || code === '') {
        this.cache.enqueue({
          conversationId, clientMessageId, body: text, replyToMessageId: options.replyToMessageId ?? null,
          ...(options.mentions?.length ? { mentions: options.mentions } : {}),
          ...(options.patients?.length ? { patients: options.patients } : {}),
          ...(files.length ? { attachments: files } : {}),
          at: new Date().toISOString(),
        });
      }
      throw e;
    }
  }


  // ── search, and opening a conversation at an older message (2026-09-20) ───────────

  /** Messages matching every word, across everything this person may read. Whole words; newest first. */
  search(query: string, limit = 30): Promise<SearchHit[]> {
    const text = query.trim();
    if (text.length < 2) return Promise.resolve([]);
    return this.hub.request<SearchHit[]>('msg:search', { query: text, limit });
  }

  /**
   * Opens a conversation showing the message a search found, even when it is far back. The newest messages are not in
   * the thread while it is `historic`; `open()` again returns to the present.
   */
  async openAt(conversationId: string, seq: number): Promise<void> {
    await this.open(conversationId);
    const loaded = this.state.value.threads[conversationId];
    if (loaded?.messages.some((m) => m.seq === seq)) {
      this.setThread(conversationId, { pinnedSeq: seq });
      return;
    }
    this.setThread(conversationId, { loading: true });
    try {
      // A page ENDING a little after the message, so it sits in view with context on both sides.
      const page = await this.hub.request<{ messages: MessageView[]; hasMore: boolean }>(
        'msg:history', { conversationId, beforeSeq: seq + Math.floor(PAGE / 4), limit: PAGE },
      );
      this.setThread(conversationId, {
        messages: page.messages.map((m) => ({ ...m })), hasMore: page.hasMore, loaded: true, loading: false, historic: true, pinnedSeq: seq,
      });
    } catch (e) {
      this.setThread(conversationId, { loading: false });
      throw e;
    }
  }

  /** Back to the newest messages after a search took the thread into the past. */
  async backToLatest(conversationId: string): Promise<void> {
    this.setThread(conversationId, { messages: [], loaded: false, historic: false, pinnedSeq: null });
    await this.open(conversationId);
  }

  // ── offline: a queue that survives a reload ──────────────────────────────────────

  /** True when the hub is not reachable right now (a send would fail). */
  private get connected(): boolean {
    return !!this.transport && !!this.state.value.me;
  }

  /** Sends everything queued while disconnected, oldest first, keeping anything that still fails. */
  private async flushQueue(): Promise<void> {
    for (const q of [...this.cache.queued]) {
      try {
        await this.send(q.conversationId, q.body, {
          clientMessageId: q.clientMessageId,
          replyToMessageId: q.replyToMessageId ?? null,
          ...(q.mentions?.length ? { mentions: q.mentions } : {}),
          ...(q.patients?.length ? { patients: q.patients } : {}),
          // send() reads whole file records; an entry queued with ids only sends them as bare records
          ...(q.attachments?.length || q.attachmentIds?.length
            ? { attachments: q.attachments ?? q.attachmentIds!.map((id) => ({ id }) as AttachmentView) }
            : {}),
        });
      } catch {
        // still not going through: it stays queued for the next connection
      }
    }
  }

  /** What is waiting to be sent from this device (shown as pending in the thread). */
  queuedMessages(): QueuedMessage[] {
    return this.cache.queued;
  }

  /** Half-written text, kept per conversation so a reload or a switch does not lose it. */
  draftOf(conversationId: string): string {
    return this.cache.draftOf(conversationId);
  }

  saveDraft(conversationId: string, text: string): void {
    this.cache.saveDraft(conversationId, text);
  }

  /** Sends a failed message again with the SAME clientMessageId, so the hub keeps one copy whatever happened. */
  async retry(conversationId: string, clientMessageId: string): Promise<void> {
    const m = this.state.value.threads[conversationId]?.messages.find((x) => x.id === clientMessageId && x.failed);
    if (!m || (!m.body && !m.attachments?.length)) return;
    await this.send(conversationId, m.body ?? '', {
      replyToMessageId: m.replyToMessageId,
      mentions: m.mentions.principals,
      clientMessageId,
      ...(m.patientMentions?.length ? { patients: m.patientMentions } : {}),
      ...(m.attachments?.length ? { attachments: m.attachments } : {}),
    });
  }

  discardFailed(conversationId: string, clientMessageId: string): void {
    this.setThread(conversationId, { messages: this.withoutId(conversationId, clientMessageId) });
  }

  private withoutId(conversationId: string, id: string): ThreadMessage[] {
    return (this.state.value.threads[conversationId]?.messages ?? []).filter((m) => m.id !== id);
  }

  async edit(messageId: string, body: string): Promise<void> {
    this.replaceMessage(await this.hub.request<MessageView>('msg:edit', { messageId, body }));
  }

  async remove(messageId: string): Promise<void> {
    this.replaceMessage(await this.hub.request<MessageView>('msg:delete', { messageId }));
  }

  /**
   * Links a message into a patient's chat as a frozen copy (§8.4); linking it to the same patient again changes
   * nothing. The copy arrives in the patient chat as msg:new; the message itself comes back with `linkedTo`.
   */
  async linkPatient(messageId: string, patient: PatientMention): Promise<MessageView> {
    const source = await this.hub.request<MessageView>('msg:linkPatient', {
      messageId,
      patientRef: patient.patientRef,
      title: patient.title,
      ...(patient.system ? { system: patient.system } : {}),
    });
    this.replaceMessage(source);
    return source;
  }

  /**
   * The external clinician's counterpart of linkPatient (2026-09-22): links a message of a network chat into the
   * referral chat about one patient, with the ticket that opens that chat (the referrals API's POST
   * /referrals/:id/chat-ticket). The hub refuses a referral that is not on the chat's own link, or no longer shared.
   */
  async linkReferral(messageId: string, ticket: string): Promise<MessageView> {
    const source = await this.hub.request<MessageView>('msg:linkReferral', { messageId, ticket });
    this.replaceMessage(source);
    return source;
  }

  /**
   * Answers a support agent's log request (the user's side). Accepting uploads `logs` first and sends them as the
   * answer; declining sends a line saying so. The request comes back answered, so its prompt goes away on every device.
   * Rejects with CONFLICT when it was already answered, or an upload code (fileErrorText has the words).
   */
  async answerLogRequest(request: MessageView, answer: { accept: true; logs: Blob; fileName: string } | { accept: false }): Promise<MessageView> {
    const attachment = answer.accept ? await this.uploadFile(request.conversationId, answer.logs, answer.fileName) : null;
    const { message, request: updated } = await this.hub.request<{ message: MessageView; request: MessageView }>('support:answerLogs', {
      messageId: request.id,
      accept: answer.accept,
      clientMessageId: newClientMessageId(),
      ...(attachment ? { attachmentIds: [attachment.id] } : {}),
    });
    const t = this.state.value.threads[request.conversationId];
    if (t?.loaded) this.setThread(request.conversationId, { messages: mergeMessages(t.messages, [updated, message]) });
    this.noteNewMessage(message);
    return message;
  }

  async react(messageId: string, emoji: string, on: boolean): Promise<void> {
    this.replaceMessage(await this.hub.request<MessageView>('msg:react', { messageId, emoji, on }));
  }

  /**
   * Forwards a message into each of these conversations (2026-10-02). Each copy is the caller's own message there, with
   * the way back to the original (`forwardedFrom`); it is shown at once where its conversation is open.
   */
  async forward(messageId: string, conversationIds: readonly string[]): Promise<MessageView[]> {
    const copies = await this.hub.request<MessageView[]>('msg:forward', { messageId, conversationIds: [...conversationIds] });
    for (const copy of copies) {
      this.replaceMessage(copy);
      this.noteNewMessage(copy);
    }
    return copies;
  }

  /** Posts a card for something made from the chat (a task for a patient) as the caller's message, shown at once. */
  async sendAction(conversationId: string, card: ActionCard): Promise<MessageView> {
    const message = await this.hub.request<MessageView>('msg:sendAction', { conversationId, clientMessageId: newClientMessageId(), card });
    this.replaceMessage(message);
    this.noteNewMessage(message);
    return message;
  }

  /** Pins a message for everyone in its conversation (at most three: PIN_LIMIT). Returns the conversation's pins now. */
  pin(messageId: string): Promise<PinView[]> {
    return this.hub.request<PinView[]>('msg:pin', { messageId });
  }

  unpin(messageId: string): Promise<PinView[]> {
    return this.hub.request<PinView[]>('msg:unpin', { messageId });
  }

  /** A conversation's pinned messages, newest pin first. */
  pins(conversationId: string): Promise<PinView[]> {
    return this.hub.request<PinView[]>('conv:pins', { conversationId });
  }

  /** The patient chats a conversation is being filed to (a link starts it, another link replaces it), or null. */
  filing(conversationId: string): Promise<FilingView | null> {
    return this.hub.request<FilingView | null>('conv:filing', { conversationId });
  }

  async endFiling(conversationId: string): Promise<void> {
    await this.hub.request('conv:endFiling', { conversationId });
  }

  /**
   * Before unlinking `messageId` (a message, or its copy) from the patient chat `conversationId`: how many later
   * messages were filed there with it, and whether the conversation is still being filed there (unlinkQuestion).
   */
  unlinkScope(messageId: string, conversationId: string): Promise<UnlinkScope> {
    return this.hub.request<UnlinkScope>('msg:unlinkScope', { messageId, conversationId });
  }

  /**
   * Takes a message filed by mistake off the patient's chat, with the later ones filed with it when `andLater`. The
   * changed messages and copies come back as msg:updated, and filing:updated when filing to that patient stopped.
   */
  unlink(messageId: string, conversationId: string, andLater = false): Promise<{ unlinked: number; filingEnded: boolean }> {
    return this.hub.request<{ unlinked: number; filingEnded: boolean }>('msg:unlink', { messageId, conversationId, ...(andLater ? { andLater } : {}) });
  }

  typing(conversationId: string, typing: boolean): void {
    void this.transport?.request('typing', { conversationId, typing }).catch(() => undefined);
  }

  private replaceMessage(m: MessageView): void {
    const t = this.state.value.threads[m.conversationId];
    if (t?.loaded) this.setThread(m.conversationId, { messages: mergeMessages(t.messages, [m]) });
  }

  /** A copy filed into a patient chat from a conversation this user is in: they already saw it there. */
  private seenElsewhere(m: MessageView): boolean {
    const from = m.kind === 'linked_copy' ? m.linkedFrom?.conversationId : null;
    return !!from && this.state.value.conversations[from]?.access === 'participant';
  }

  /** Updates the conversation's last message and unread count for a message that just arrived. */
  private noteNewMessage(m: MessageView): void {
    const c = this.state.value.conversations[m.conversationId];
    if (!c) return;
    const mine = m.sender?.principalId === this.state.value.me?.principalId;
    const reading = this.state.value.activeConversationId === m.conversationId && this.visible;
    const newer = c.lastMessageSeq == null || m.seq > c.lastMessageSeq;
    this.upsertConversation({
      ...c,
      ...(newer ? { lastMessageSeq: m.seq, lastMessageAt: m.createdAt, lastMessagePreview: m.kind === 'forwarded' ? `Forwarded: ${m.body ?? ''}` : m.body } : {}),
      unread: mine || reading ? 0 : c.unread + (newer && m.seq > c.lastReadSeq && !this.seenElsewhere(m) ? 1 : 0),
      ...(mine ? { lastReadSeq: Math.max(c.lastReadSeq, m.seq) } : {}),
    });
    if (reading && !mine) void this.markReadToEnd(m.conversationId);
  }

  // ── creating and managing ─────────────────────────────────────────────────────────

  async createChannel(input: { orgId: string; name: string; description?: string; visibility: 'public' | 'private'; memberIds: string[] }): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:createChannel', input);
    this.upsertConversation(c);
    return c;
  }

  /** The practice's chat about a patient (created on first open); the caller joins it. */
  async openPatient(orgId: string, patientRef: string, title: string, system?: 'clinical' | 'connect'): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:openPatient', { orgId, patientRef, title, ...(system ? { system } : {}) });
    this.upsertConversation(c);
    return c;
  }

  /**
   * A referral chat (M6), opened with the one-use ticket this app's server signed for it (who may chat, on which side,
   * about which patient). The caller joins; the conversation is listed like any other.
   */
  async openReferral(ticket: string): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:openReferral', { ticket });
    this.upsertConversation(c);
    return c;
  }

  /**
   * A 1-on-1 across the referral network (2026-09-22), opened with the one-use ticket this app's server signed (the link
   * is active and the other person is on it). Both people are in it from the start.
   */
  async openNetwork(ticket: string): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:openNetwork', { ticket });
    this.upsertConversation(c);
    return c;
  }

  /**
   * The signed-in user's conversation with MedicAI Support (M7), found or created, seen by every support agent: one per
   * user per org from the clinical app and Connect, one per person with no org from the referrals dashboard and the
   * patient app. Support keeps messages as sent (no edit or delete).
   */
  async openSupport(orgId?: string): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:openSupport', orgId ? { orgId } : {});
    this.upsertConversation(c);
    return c;
  }

  /** Messages per day in a patient's chat, in this browser's time zone (for the timeline). */
  patientActivity(orgId: string, patientRef: string): Promise<PatientChatActivity> {
    return this.hub.request<PatientChatActivity>('patient:activity', { orgId, patientRef, tzOffsetMinutes: -new Date().getTimezoneOffset() });
  }

  async openDirect(orgId: string, otherId: string): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:openDirect', { orgId, otherId });
    this.upsertConversation(c);
    return c;
  }

  async createGroup(orgId: string, memberIds: string[]): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('conv:createGroup', { orgId, memberIds });
    this.upsertConversation(c);
    return c;
  }

  /** Billing chats (2026-10-05): the relations of this user's orgs, each with its standing room. */
  billingRelations(): Promise<BillingRelation[]> {
    return this.hub.request<BillingRelation[]>('billing:relations', {});
  }

  /** The people on the other side of a live billing relation, for the 1:1 and group pickers. */
  billingDirectory(orgId: string, otherOrgId: string): Promise<BillingPerson[]> {
    return this.hub.request('billing:directory', { orgId, otherOrgId });
  }

  /** The billing chats this user is in where the patient was tagged, latest first (a money card's Chat button). */
  async billingPatientChats(orgId: string, patient: PatientMention): Promise<ConversationSummary[]> {
    const chats = await this.hub.request<ConversationSummary[]>('billing:patientChats', {
      orgId, patientRef: patient.patientRef, system: patient.system ?? 'clinical',
    });
    for (const c of chats) this.upsertConversation(c);
    return chats;
  }

  /** Starts a chat across a billing relation: nobody picked is its room, one person a 1:1, several a group. Its id. */
  async startBillingChat(relation: Pick<BillingRelation, 'orgId' | 'otherOrgId' | 'roomId'>, memberIds: string[], name?: string): Promise<string> {
    if (!memberIds.length) return relation.roomId;
    const c = memberIds.length === 1
      ? await this.openBillingDirect(relation.orgId, relation.otherOrgId, memberIds[0]!)
      : await this.createBillingGroup(relation.orgId, relation.otherOrgId, memberIds, name);
    return c.id;
  }

  /** A patient to tag in a conversation's message box when the thread next opens it (a money card's Chat button). */
  prefillPatient(conversationId: string, patient: PatientMention): void {
    this.prefills.set(conversationId, patient);
    this.prefilled.next(conversationId);
  }

  /** The patient waiting to be tagged in this conversation, once. */
  takePrefill(conversationId: string): PatientMention | null {
    const patient = this.prefills.get(conversationId) ?? null;
    this.prefills.delete(conversationId);
    return patient;
  }

  /** The 1:1 with someone on the other side of a billing relation, found or created. */
  async openBillingDirect(orgId: string, otherOrgId: string, otherId: string): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('billing:openDirect', { orgId, otherOrgId, otherId });
    this.upsertConversation(c);
    return c;
  }

  /** A group across a billing relation: people from its two sides only. */
  async createBillingGroup(orgId: string, otherOrgId: string, memberIds: string[], name?: string): Promise<ConversationSummary> {
    const c = await this.hub.request<ConversationSummary>('billing:createGroup', { orgId, otherOrgId, memberIds, ...(name ? { name } : {}) });
    this.upsertConversation(c);
    return c;
  }

  async join(id: string): Promise<void> {
    this.upsertConversation(await this.hub.request<ConversationSummary>('conv:join', { conversationId: id }));
  }

  async leave(id: string): Promise<void> {
    const me = this.state.value.me;
    if (!me) return;
    await this.hub.request('conv:removeMember', { conversationId: id, principalId: me.principalId });
    await this.refreshConversation(id);
  }

  async archive(id: string, archived: boolean): Promise<void> {
    this.upsertConversation(await this.hub.request<ConversationSummary>('conv:archive', { conversationId: id, archived }));
  }

  async deleteChannel(id: string): Promise<void> {
    await this.hub.request('conv:delete', { conversationId: id });
    this.removeConversation(id);
  }

  /** Which of this conversation's messages push to my devices (M4). */
  async setNotifyLevel(id: string, level: NotifyLevel): Promise<void> {
    await this.hub.request('conv:notify', { conversationId: id, level });
    const c = this.state.value.conversations[id];
    if (c) this.upsertConversation({ ...c, notifyLevel: level });
  }

  /** Whether I have muted every patient-app chat (one switch, for all of them, including those that start later). */
  async patientChatsMuted(): Promise<boolean> {
    return (await this.hub.request<{ mutePatientChats: boolean }>('prefs:get', {})).mutePatientChats;
  }

  /** Mutes (until I unmute) or unmutes every patient-app chat I am in, and those that start later. */
  async setPatientChatsMuted(on: boolean): Promise<void> {
    await this.hub.request('prefs:set', { mutePatientChats: on });
    await this.refreshList();
  }

  // ── files (M5) ─────────────────────────────────────────────────────────────────────

  /**
   * Uploads one file for a message in this conversation, resumably (file-upload.ts: chunks with retry, progress,
   * cancel; the same Blob resumes after a failure). The result goes into send(..., { attachments }). Rejects with a
   * code: FILE_TYPE, FILE_TOO_LARGE, FILE_EMPTY, CANCELLED, UPLOAD_FAILED, NOT_CONNECTED (fileErrorText has the words).
   */
  uploadFile(conversationId: string, file: Blob, fileName: string, options: UploadOptions = {}): Promise<AttachmentView> {
    return uploadToHub({
      request: <T>(event: string, payload: unknown) => this.hub.request<T>(event, payload),
      baseUrl: () => this.transport?.baseUrl?.() ?? null,
    }, conversationId, file, fileName, options);
  }

  /** A URL for one file, good for a few minutes (use it at once: an <img src>, a link, a download). */
  async fileUrl(attachmentId: string): Promise<string> {
    const base = this.transport?.baseUrl?.();
    if (!base) throw new HubRequestError('NOT_CONNECTED');
    const { path } = await this.hub.request<{ path: string }>('attach:url', { attachmentId });
    return `${base}${path}`;
  }

  // ── push (M4): this device ────────────────────────────────────────────────────────

  /** The web push key to subscribe with; null when the hub offers no web push. */
  async pushConfig(): Promise<{ webPublicKey: string | null }> {
    return this.hub.request<{ webPublicKey: string | null }>('push:config', {});
  }

  pushRegister(registration: PushRegistration): Promise<void> {
    return this.hub.request('push:register', registration);
  }

  pushUnregister(installationId: string): Promise<void> {
    return this.hub.request('push:unregister', { installationId });
  }

  async pushStatus(installationId: string): Promise<boolean> {
    return (await this.hub.request<{ registered: boolean }>('push:status', { installationId })).registered;
  }

  async rename(id: string, name: string, description: string | null): Promise<void> {
    this.upsertConversation(await this.hub.request<ConversationSummary>('conv:update', { conversationId: id, name, description }));
  }

  /**
   * Pins a conversation to the top of my own list, or unpins it; the hub refuses a sixth in a practice (PIN_LIMIT),
   * which is passed on and changes nothing here.
   */
  async pinConversation(id: string, pinned: boolean): Promise<void> {
    await this.hub.request('conv:pin', { conversationId: id, pinned });
    await this.refreshConversation(id);
  }

  async mute(id: string, until: Date | null): Promise<void> {
    await this.hub.request('conv:mute', { conversationId: id, until: until ? until.toISOString() : null });
    await this.refreshConversation(id);
  }

  members(id: string): Promise<MemberView[]> {
    return this.hub.request<MemberView[]>('conv:members', { conversationId: id });
  }

  addMembers(id: string, memberIds: string[]): Promise<{ added: string[] }> {
    return this.hub.request<{ added: string[] }>('conv:addMembers', { conversationId: id, memberIds });
  }

  removeMember(id: string, principalId: string): Promise<{ removed: boolean }> {
    return this.hub.request<{ removed: boolean }>('conv:removeMember', { conversationId: id, principalId });
  }

  directory(orgId: string): Promise<{ principalId: string; name: string; role: string }[]> {
    return this.hub.request('org:directory', { orgId });
  }

  /** Forwards a roster token (signed by this app) so every colleague becomes pickable. */
  syncRoster(token: string): Promise<{ orgs: number; members: number }> {
    return this.hub.request('org:syncRoster', { token });
  }

  browse(orgId: string): Promise<ConversationSummary[]> {
    return this.hub.request<ConversationSummary[]>('conv:browse', { orgId });
  }

  // ── server events ─────────────────────────────────────────────────────────────────

  private onEvent({ event, data }: HubEvent): void {
    switch (event) {
      case 'msg:new': {
        const m = (data as { message: MessageView }).message;
        const fromSomeoneElse = !!m.sender && m.sender.principalId !== this.state.value.me?.principalId && !this.seenElsewhere(m);
        if (!this.state.value.conversations[m.conversationId]) {
          void this.refreshConversation(m.conversationId)
            .then(() => { if (fromSomeoneElse && this.state.value.conversations[m.conversationId]) this.incoming.next(m); })
            .catch(() => undefined);
          return;
        }
        const t = this.state.value.threads[m.conversationId];
        if (t?.loaded) this.setThread(m.conversationId, { messages: mergeMessages(t.messages, [m]) });
        if (m.sender) this.clearTyping(m.conversationId, m.sender.principalId);
        this.noteNewMessage(m);
        // It is on this device now (ticks); reading it, if the window is open, follows in noteNewMessage.
        this.noteDelivered(m.conversationId, m.seq);
        if (fromSomeoneElse) this.incoming.next(m);
        return;
      }
      case 'pins:updated':
        this.pinsChanged.next((data as { conversationId: string }).conversationId);
        return;
      case 'filing:updated':
        this.filingChanged.next((data as { conversationId: string }).conversationId);
        return;
      case 'msg:updated':
        this.replaceMessage((data as { message: MessageView }).message);
        return;
      case 'conv:updated':
        void this.refreshConversation((data as { conversationId: string }).conversationId).catch(() => undefined);
        return;
      case 'receipt:updated': {
        // One person moved: keep their marks, and let marksOf decide what the sender's ticks show.
        const d = data as { conversationId: string; principalId: string; deliveredSeq?: number; readSeq?: number };
        if (d.principalId === this.state.value.me?.principalId) return;
        this.set((st) => {
          const forConv = { ...(st.receipts[d.conversationId] ?? {}) };
          const before = forConv[d.principalId] ?? { deliveredSeq: 0, readSeq: 0 };
          forConv[d.principalId] = {
            deliveredSeq: Math.max(before.deliveredSeq, d.deliveredSeq ?? 0, d.readSeq ?? 0),
            readSeq: Math.max(before.readSeq, d.readSeq ?? 0),
          };
          return { receipts: { ...st.receipts, [d.conversationId]: forConv } };
        });
        return;
      }
      case 'avatar:changed': {
        // Someone set or removed their photo: every list and thread on this page follows.
        const d = data as { principalId: string; avatar: string | null };
        this.setAvatarOf(d.principalId, d.avatar);
        return;
      }
      case 'conv:removed':
        this.removeConversation((data as { conversationId: string }).conversationId);
        return;
      case 'read:updated': {
        const { conversationId, lastReadSeq } = data as { conversationId: string; lastReadSeq: number };
        const c = this.state.value.conversations[conversationId];
        if (c && lastReadSeq > c.lastReadSeq) {
          const caughtUp = c.lastMessageSeq == null || lastReadSeq >= c.lastMessageSeq;
          this.upsertConversation({ ...c, lastReadSeq, unread: caughtUp ? 0 : c.unread });
          if (!caughtUp) void this.refreshConversation(conversationId).catch(() => undefined);
        }
        return;
      }
      case 'typing': {
        const d = data as { conversationId: string; principalId: string; name: string; typing: boolean };
        if (d.typing) {
          this.set((s) => ({
            typing: { ...s.typing, [d.conversationId]: { ...(s.typing[d.conversationId] ?? {}), [d.principalId]: { name: d.name, until: Date.now() + TYPING_MS } } },
          }));
        } else {
          this.clearTyping(d.conversationId, d.principalId);
        }
        return;
      }
      case 'presence': {
        const d = data as { principalId: string; online: boolean };
        this.set((s) => {
          const online = { ...s.online };
          if (d.online) online[d.principalId] = true;
          else delete online[d.principalId];
          return { online };
        });
        return;
      }
      default:
        return;
    }
  }

  private clearTyping(conversationId: string, principalId: string): void {
    const current = this.state.value.typing[conversationId];
    if (!current?.[principalId]) return;
    this.set((s) => {
      const { [principalId]: _gone, ...rest } = s.typing[conversationId] ?? {};
      return { typing: { ...s.typing, [conversationId]: rest } };
    });
  }

  /** Who is typing in `conversationId` right now (entries expire after a few seconds without a new signal). */
  typists(conversationId: string, now = Date.now()): string[] {
    return Object.values(this.state.value.typing[conversationId] ?? {})
      .filter((t) => t.until > now)
      .map((t) => t.name);
  }
}
